import React, { useEffect, useMemo, useState } from 'react';
import { Accordion } from 'react-bootstrap';
import moment from 'moment';
import _debounce from 'lodash/debounce';

import { useGetMonthlyViolationsQuery } from '../../../../redux/apis/attendanceViolationApi';
import EmptyData from '../../../../components/UI/EmptyData';
import {
  buildViolationFilterParams,
  filterViolationSummaries,
  getDismissableViolationIds,
  getSortedViolations,
  getViolationPeriodLabel,
  getSummaryRowId,
  getViolationActionConfig,
  getViolationEmployeeName,
  getViolationEmployeeSubtext,
  getViolationId,
  getViolationRowId,
  getWarningStatusConfig,
  isViolationDismissable,
  isViolationLeaveApplied,
  mergeViolationForApplyLeave,
  normalizeMonthlyViolationsResponse,
  userNeedsHrAction,
  violationDateNumToIstStartMs
} from '../../../../utils/attendanceViolationHelpers';
import { skipRow } from '../../../../utils/handler';

import ViolationTimeMeta from './ViolationTimeMeta';
import ViolationTypeChip from './ViolationTypeChip';
import ViolationRowActions from './ViolationRowActions';
import { ViolationListSkeleton, ViolationStatsSkeleton } from './ViolationSkeletons';
import ViolationPeriodFilters from './ViolationPeriodFilters';
import ViolationTimeLegend from './ViolationTimeLegend';
import ViolationBulkActionBar from './ViolationBulkActionBar';

const StatusBadge = ({ status, size }) => {
  const config = getWarningStatusConfig(status);
  return (
    <span
      className={`att-violation-status att-violation-status--${config.color}${
        size ? ` att-violation-status--${size}` : ''
      }`}
    >
      {config.label}
    </span>
  );
};

const UserHeaderStatus = ({ userRow }) => {
  const needsAttention =
    userRow?.warningStatus === 'HR_NOTIFIED' ||
    userRow?.showApplyLeave ||
    userRow?.warningStatus === 'LEAVE_APPLIED';

  if (needsAttention) {
    return <StatusBadge status={userRow?.warningStatus} />;
  }

  return (
    <div className="att-violation-acc-type-chips">
      {userRow.violationSummaries.map((summary) => (
        <ViolationTypeChip
          key={summary.violationType}
          violationType={summary.violationType}
          count={summary?.monthlyViolationCount}
          compact
        />
      ))}
    </div>
  );
};

const ViolationDateBadge = ({ dateNum, typeClass }) => {
  const ms = violationDateNumToIstStartMs(dateNum);
  if (!ms) return null;
  const m = moment(ms).utcOffset(330);

  return (
    <div className={`att-violation-date-badge att-violation-date-badge--${typeClass || 'default'}`}>
      <span className="att-violation-date-badge__dow">{m.format('ddd')}</span>
      <span className="att-violation-date-badge__day">{m.format('DD')}</span>
      <span className="att-violation-date-badge__month">{m.format('MMM')}</span>
    </div>
  );
};

const ViolationTimeline = ({
  userRow,
  summary,
  onApplyLeave,
  onActionSuccess,
  selectedIds,
  onToggleSelect
}) => {
  const violations = getSortedViolations(summary);
  const typeClass = summary?.violationType === 'EARLY_EXIT' ? 'exit' : 'late';

  if (!violations.length) {
    return <p className="att-violation-history__empty">No violation dates found.</p>;
  }

  return (
    <div className={`att-violation-timeline att-violation-timeline--${typeClass}`}>
      {violations.map((violation, index) => {
        const isPending = violation?.violationId === summary?.pendingViolationId;
        const actionConfig = getViolationActionConfig(violation);
        const violationId = getViolationId(violation);
        const selectable = isViolationDismissable(summary, violation);
        const isSelected = selectable && selectedIds.has(violationId);

        return (
          <div
            key={violation?.violationId || `${violation?.date}-${violation?.monthlyCount}`}
            className={`att-violation-timeline__item${
              isPending ? ' att-violation-timeline__item--pending' : ''
            }${isSelected ? ' att-violation-timeline__item--selected' : ''}`}
          >
            <div className="att-violation-timeline__rail" aria-hidden>
              <span
                className={`att-violation-timeline__dot att-violation-timeline__dot--${actionConfig.color}`}
              />
              {index < violations.length - 1 ? (
                <span className="att-violation-timeline__line" />
              ) : null}
            </div>

            <div className="att-violation-timeline__card att-violation-timeline__card--selectable">
              <span className="att-violation-timeline__select">
                {selectable ? (
                  <input
                    type="checkbox"
                    className="form-check-input att-violation-check"
                    checked={isSelected}
                    onChange={() => onToggleSelect([violationId])}
                    aria-label={`Select violation #${violation?.monthlyCount ?? ''}`}
                  />
                ) : null}
              </span>
              <ViolationDateBadge dateNum={violation?.date} typeClass={typeClass} />
              <div className="att-violation-timeline__content">
                <span className="att-violation-timeline__count">
                  Violation #{violation?.monthlyCount ?? '—'}
                </span>
                <ViolationTimeMeta violation={violation} />
              </div>
              <div className="att-violation-timeline__actions">
                <ViolationRowActions
                  violation={violation}
                  summary={summary}
                  onApplyLeave={() => onApplyLeave(userRow, summary, violation)}
                  onActionSuccess={onActionSuccess}
                />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const ViolationSummarySection = ({
  userRow,
  summary,
  onApplyLeave,
  onActionSuccess,
  selectedIds,
  onToggleSelect
}) => {
  const violations = getSortedViolations(summary);
  const leaveApplied = isViolationLeaveApplied(summary);
  const typeClass = summary?.violationType === 'EARLY_EXIT' ? 'exit' : 'late';
  const dismissableIds = getDismissableViolationIds(summary);
  const selectedInSection = dismissableIds.filter((id) => selectedIds.has(id)).length;
  const allSelected = dismissableIds.length > 0 && selectedInSection === dismissableIds.length;

  const handleToggleSection = () => {
    onToggleSelect(
      allSelected ? dismissableIds : dismissableIds.filter((id) => !selectedIds.has(id))
    );
  };

  return (
    <section className={`att-violation-summary att-violation-summary--${typeClass}`}>
      <div className="att-violation-summary__head">
        <div className="att-violation-summary__title-wrap">
          <ViolationTypeChip
            violationType={summary?.violationType}
            count={summary?.monthlyViolationCount ?? violations.length}
          />
        </div>
        {leaveApplied || dismissableIds.length > 1 ? (
          <div className="att-violation-summary__actions">
            {dismissableIds.length > 1 ? (
              <label className="att-violation-summary__select-all">
                <input
                  type="checkbox"
                  className="form-check-input att-violation-check"
                  checked={allSelected}
                  ref={(el) => {
                    if (el) el.indeterminate = selectedInSection > 0 && !allSelected;
                  }}
                  onChange={handleToggleSection}
                />
                <span>Select all</span>
              </label>
            ) : null}
            {leaveApplied ? (
              <span className="att-violation-action att-violation-action--done">
                <i className="bi bi-check-circle-fill" aria-hidden /> Leave applied
              </span>
            ) : null}
          </div>
        ) : null}
      </div>
      <ViolationTimeline
        userRow={userRow}
        summary={summary}
        onApplyLeave={onApplyLeave}
        onActionSuccess={onActionSuccess}
        selectedIds={selectedIds}
        onToggleSelect={onToggleSelect}
      />
    </section>
  );
};

const ViolationPager = ({ state, setState, pageData, loading }) => {
  const totalPages = Math.max(1, Math.ceil(pageData.totalCount / state.rowsPerPage));
  const rangeStart = pageData.totalCount ? skipRow(state.currentPage, state.rowsPerPage) + 1 : 0;
  const rangeEnd = Math.min(state.currentPage * state.rowsPerPage, pageData.totalCount);

  if (!pageData.totalCount) return null;

  return (
    <div className={`att-violation-pager${loading ? ' att-violation-pager--loading' : ''}`}>
      <p className="att-violation-pager__summary">
        Showing <strong>{rangeStart}</strong>–<strong>{rangeEnd}</strong> of{' '}
        <strong>{pageData.totalCount}</strong> employees
      </p>
      <div className="att-violation-pager__track">
        <button
          type="button"
          className="att-violation-pager__btn"
          disabled={loading || state.currentPage <= 1}
          onClick={() => setState((prev) => ({ ...prev, currentPage: prev.currentPage - 1 }))}
          aria-label="Previous page"
        >
          <i className="bi bi-chevron-left" aria-hidden />
        </button>
        <span className="att-violation-pager__page">
          Page <strong>{state.currentPage}</strong> / <strong>{totalPages}</strong>
        </span>
        <button
          type="button"
          className="att-violation-pager__btn"
          disabled={loading || state.currentPage >= totalPages}
          onClick={() => setState((prev) => ({ ...prev, currentPage: prev.currentPage + 1 }))}
          aria-label="Next page"
        >
          <i className="bi bi-chevron-right" aria-hidden />
        </button>
      </div>
    </div>
  );
};

const ViolationTable = ({ state, setState, onApplyLeave, onActionSuccess }) => {
  const queryParams = useMemo(
    () => ({
      ...buildViolationFilterParams(state),
      skip: skipRow(state.currentPage, state.rowsPerPage),
      limit: state.rowsPerPage
    }),
    [
      state.periodType,
      state.date,
      state.startDate,
      state.endDate,
      state.month,
      state.year,
      state.violationType,
      state.search,
      state.currentPage,
      state.rowsPerPage
    ]
  );

  const { data, isLoading, isFetching, isUninitialized, refetch } =
    useGetMonthlyViolationsQuery(queryParams);

  const pageData = normalizeMonthlyViolationsResponse(data);
  const listRows = useMemo(() => {
    return pageData.items
      .map((row) => ({
        ...row,
        id: getViolationRowId(row),
        violationSummaries: filterViolationSummaries(row, state.violationType)
      }))
      .filter((row) => row.violationSummaries.length > 0);
  }, [pageData.items, state.violationType]);

  const [selectedIds, setSelectedIds] = useState(() => new Set());

  const selectableIds = useMemo(
    () =>
      Array.from(
        new Set(
          listRows.flatMap((row) =>
            row.violationSummaries.flatMap((summary) => getDismissableViolationIds(summary))
          )
        )
      ),
    [listRows]
  );

  useEffect(() => {
    setSelectedIds((prev) => {
      if (!prev.size) return prev;
      const allowed = new Set(selectableIds);
      const next = new Set([...prev].filter((id) => allowed.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [selectableIds]);

  const handleToggleSelect = (ids) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => (next.has(id) ? next.delete(id) : next.add(id)));
      return next;
    });
  };

  const handleBulkResult = ({ failed }) => {
    setSelectedIds(new Set(failed));
  };

  const isPending = isUninitialized || isLoading || isFetching;
  const showSkeleton = isPending;
  const hrActionCount = useMemo(
    () => pageData.items.filter((row) => userNeedsHrAction(row)).length,
    [pageData.items]
  );

  useEffect(() => {
    if (!data) return;
    const { isNext } = normalizeMonthlyViolationsResponse(data);
    setState((prev) => (prev.isNext === isNext ? prev : { ...prev, isNext }));
  }, [data, setState]);

  const debouncedSearch = _debounce((value) => {
    setState((prev) => ({ ...prev, search: value, currentPage: 1 }));
  }, 400);

  const periodLabel = useMemo(() => getViolationPeriodLabel(state), [state]);

  const handleApplyLeave = (userRow, summary, violation) => {
    onApplyLeave(mergeViolationForApplyLeave(userRow, summary, violation));
  };

  return (
    <div className="att-violation-table-wrap">
      <div className="att-violation-toolbar">
        <div className="att-violation-filter-bar">
          <ViolationPeriodFilters state={state} setState={setState} />

          <div className="att-violation-filter-bar__secondary">
            <div className="att-violation-filter-field att-violation-filter-field--type">
              <label className="att-violation-filter-field__label" htmlFor="att-violation-type">
                Type
              </label>
              <select
                id="att-violation-type"
                className="form-select form-select-sm att-violation-control"
                value={state.violationType}
                onChange={(e) =>
                  setState((prev) => ({ ...prev, violationType: e.target.value, currentPage: 1 }))
                }
              >
                <option value="all">All types</option>
                <option value="LATE_ARRIVAL">Late Arrival</option>
                <option value="EARLY_EXIT">Early Exit</option>
              </select>
            </div>

            <div className="att-violation-filter-field att-violation-filter-field--search">
              <label className="att-violation-filter-field__label" htmlFor="att-violation-search">
                Search
              </label>
              <div className="att-violation-search">
                <i className="bi bi-search att-violation-search__icon" aria-hidden />
                <input
                  id="att-violation-search"
                  className="form-control form-control-sm att-violation-search__input att-violation-control"
                  placeholder="Search employee…"
                  defaultValue={state.search}
                  onChange={(e) => debouncedSearch(e.target.value)}
                />
              </div>
            </div>

            <button
              type="button"
              className={`btn btn-sm att-violation-filter-bar__refresh${
                isFetching ? ' att-violation-filter-bar__refresh--loading' : ''
              }`}
              onClick={() => refetch()}
              disabled={isFetching}
              aria-label="Refresh violations"
            >
              <i className="bi bi-arrow-repeat" aria-hidden />
              <span>Refresh</span>
            </button>
          </div>

          <div className="att-violation-filter-bar__legend-row">
            <ViolationTimeLegend />
          </div>
        </div>
      </div>

      {showSkeleton ? (
        <ViolationStatsSkeleton />
      ) : (
        <div className="att-violation-stats">
          <div className="att-violation-stats__card">
            <span className="att-violation-stats__label">Period</span>
            <strong className="att-violation-stats__value">{periodLabel}</strong>
          </div>
          <div className="att-violation-stats__card">
            <span className="att-violation-stats__label">Total employees</span>
            <strong className="att-violation-stats__value">{pageData.totalCount}</strong>
          </div>
          <div className="att-violation-stats__card att-violation-stats__card--alert">
            <span className="att-violation-stats__label">HR action required</span>
            <strong className="att-violation-stats__value">{hrActionCount}</strong>
          </div>
        </div>
      )}

      {showSkeleton ? (
        <ViolationListSkeleton rows={6} />
      ) : !listRows.length ? (
        <EmptyData />
      ) : (
        <div className="att-violation-accordion-wrap">
          <ViolationBulkActionBar
            selectedIds={[...selectedIds]}
            selectableIds={selectableIds}
            onSelectAll={() => setSelectedIds(new Set(selectableIds))}
            onClear={() => setSelectedIds(new Set())}
            onResult={handleBulkResult}
          />
          <Accordion alwaysOpen={false}>
            {listRows.map((userRow) => {
              const profilePicture = userRow?.user?.profilePicture;
              const totalViolations = userRow?.totalViolationsThisMonth ?? 0;
              const userDismissableIds = userRow.violationSummaries.flatMap((summary) =>
                getDismissableViolationIds(summary)
              );
              const userSelectedCount = userDismissableIds.filter((id) =>
                selectedIds.has(id)
              ).length;
              const userAllSelected =
                userDismissableIds.length > 0 && userSelectedCount === userDismissableIds.length;

              return (
                <Accordion.Item
                  key={userRow.id}
                  eventKey={userRow.id}
                  className={`att-violation-accordion-item att-violation-accordion-item--selectable${
                    userSelectedCount ? ' att-violation-accordion-item--selected' : ''
                  }`}
                >
                  <span className="att-violation-acc-select">
                    {userDismissableIds.length ? (
                      <input
                        type="checkbox"
                        className="form-check-input att-violation-check"
                        checked={userAllSelected}
                        ref={(el) => {
                          if (el) el.indeterminate = userSelectedCount > 0 && !userAllSelected;
                        }}
                        onChange={() =>
                          handleToggleSelect(
                            userAllSelected
                              ? userDismissableIds
                              : userDismissableIds.filter((id) => !selectedIds.has(id))
                          )
                        }
                        aria-label={`Select all violations of ${getViolationEmployeeName(userRow)}`}
                        title="Select all violations of this employee"
                      />
                    ) : null}
                  </span>
                  <Accordion.Header className="att-violation-accordion-btn">
                    <div className="att-violation-acc-head">
                      <div className="att-violation-acc-user">
                        <div className="att-violation-acc-user__avatar-wrap">
                          {profilePicture ? (
                            <img
                              className="att-violation-acc-user__avatar"
                              src={profilePicture}
                              alt=""
                            />
                          ) : (
                            <span className="att-violation-acc-user__avatar att-violation-acc-user__avatar--placeholder">
                              {(getViolationEmployeeName(userRow) || '?').slice(0, 1).toUpperCase()}
                            </span>
                          )}
                          {totalViolations > 0 ? (
                            <span className="att-violation-acc-user__count">{totalViolations}</span>
                          ) : null}
                        </div>
                        <span className="att-violation-acc-user__meta">
                          <span className="att-violation-acc-user__name">
                            {getViolationEmployeeName(userRow)}
                          </span>
                          <span className="att-violation-acc-user__role">
                            {getViolationEmployeeSubtext(userRow)}
                          </span>
                        </span>
                      </div>

                      <div className="att-violation-acc-actions">
                        <UserHeaderStatus userRow={userRow} />
                      </div>
                    </div>
                  </Accordion.Header>

                  <Accordion.Body>
                    <div className="att-violation-summaries">
                      {userRow.violationSummaries.map((summary) => (
                        <ViolationSummarySection
                          key={getSummaryRowId(userRow, summary)}
                          userRow={userRow}
                          summary={summary}
                          onApplyLeave={handleApplyLeave}
                          onActionSuccess={onActionSuccess}
                          selectedIds={selectedIds}
                          onToggleSelect={handleToggleSelect}
                        />
                      ))}
                    </div>
                  </Accordion.Body>
                </Accordion.Item>
              );
            })}
          </Accordion>
        </div>
      )}

      {!showSkeleton ? (
        <ViolationPager state={state} setState={setState} pageData={pageData} loading={isPending} />
      ) : null}
    </div>
  );
};

export default ViolationTable;
